import React from 'react';
import { Navigate } from 'react-router-dom';
import { PrivateRoute } from 'shared/components/PrivateRoute';
import { paths, routeSegments } from 'shared/routing/paths';
import { ManagerProvider } from 'features/manager/contexts/ManagerContext';
import { ManagerLayout } from 'features/manager/components/ManagerLayout';
import { defineRoute, AppRoute } from './types';
import { lazyNamed } from './lazy';

const ManagerProjectsPage = lazyNamed(
  () => import('features/manager/pages/ManagerProjectsPage'),
  'ManagerProjectsPage',
);
const ManagerCreateProjectPage = lazyNamed(
  () => import('features/manager/pages/ManagerCreateProjectPage'),
  'ManagerCreateProjectPage',
);
const ManagerProjectJobListPage = lazyNamed(
  () => import('features/manager/pages/ManagerProjectJobListPage'),
  'ManagerProjectJobListPage',
);
const ManagerCreateJobPage = lazyNamed(
  () => import('features/manager/pages/ManagerCreateJobPage'),
  'ManagerCreateJobPage',
);
const ManagerJobPickerPage = lazyNamed(
  () => import('features/manager/pages/ManagerJobPickerPage'),
  'ManagerJobPickerPage',
);
const ManagerDashPage = lazyNamed(() => import('features/manager/pages/ManagerDashPage'), 'ManagerDashPage');
const TaskTemplateTablePage = lazyNamed(
  () => import('features/manager/pages/TaskTemplateTablePage'),
  'TaskTemplateTablePage',
);
const TemplateBuilderPage = lazyNamed(
  () => import('features/manager/pages/TemplateBuilderPage'),
  'TemplateBuilderPage',
);
const ManagerSettingsPage = lazyNamed(
  () => import('features/manager/pages/ManagerSettingsPage'),
  'ManagerSettingsPage',
);
const ManagerBillingPage = lazyNamed(() => import('features/manager/pages/ManagerBillingPage'), 'ManagerBillingPage');
const ManagerDatasetsPage = lazyNamed(
  () => import('features/manager/pages/ManagerDatasetsPage'),
  'ManagerDatasetsPage',
);
const ManagerAutoTunePage = lazyNamed(
  () => import('features/manager/pages/ManagerAutoTunePage'),
  'ManagerAutoTunePage',
);
const ManagerDatasetCreatePage = lazyNamed(
  () => import('features/manager/pages/ManagerDatasetCreatePage'),
  'ManagerDatasetCreatePage',
);
const ManagerDatasetExplorePage = lazyNamed(
  () => import('features/manager/pages/ManagerDatasetExplorePage'),
  'ManagerDatasetExplorePage',
);
const ManagerDatasetEditPage = lazyNamed(
  () => import('features/manager/pages/ManagerDatasetEditPage'),
  'ManagerDatasetEditPage',
);

const managerRootId = 'manager.shell';

function managerChild(
  id: string,
  path: string,
  element: React.ReactNode,
  layout: 'shell' | 'full-width' | 'full-height' = 'shell',
): AppRoute {
  return defineRoute(
    {
      id,
      path,
      area: 'manager',
      guard: 'inherited',
      layout,
      kind: 'page',
      parentId: managerRootId,
    },
    { path: path.slice(paths.manager.root.length + 1), element },
  );
}

const managerRootRoute = defineRoute(
  {
    id: managerRootId,
    path: paths.manager.root,
    area: 'manager',
    guard: 'manager',
    layout: 'shell',
    kind: 'layout',
  },
  {
    path: paths.manager.root,
    element: (
      <PrivateRoute requireRole="manager">
        <ManagerProvider>
          <ManagerLayout />
        </ManagerProvider>
      </PrivateRoute>
    ),
    children: [
      defineRoute(
        {
          id: 'manager.index',
          path: paths.manager.root,
          area: 'manager',
          guard: 'inherited',
          layout: 'shell',
          kind: 'redirect',
          parentId: managerRootId,
          redirectTo: paths.manager.projects,
        },
        { index: true, element: <Navigate to={paths.manager.projects} replace /> },
      ),
      managerChild('manager.projects', paths.manager.projects, <ManagerProjectsPage />),
      managerChild('manager.createProject', paths.manager.createProject, <ManagerCreateProjectPage />),
      managerChild(
        'manager.project',
        paths.manager.root + '/' + routeSegments.manager.project,
        <ManagerProjectJobListPage />,
      ),
      managerChild(
        'manager.createJobForProject',
        paths.manager.root + '/' + routeSegments.manager.createJobForProject,
        <ManagerCreateJobPage />,
      ),
      managerChild('manager.createJob', paths.manager.createJob, <ManagerCreateJobPage />),
      managerChild('manager.pickJobType', paths.manager.pickJobType, <ManagerJobPickerPage />),
      managerChild(
        'manager.dashboard',
        paths.manager.root + '/' + routeSegments.manager.dashboard,
        <ManagerDashPage />,
      ),
      managerChild('manager.taskTemplates', paths.manager.taskTemplates, <TaskTemplateTablePage />),
      managerChild(
        'manager.templateBuilder',
        paths.manager.root + '/' + routeSegments.manager.templateBuilder,
        <TemplateBuilderPage />,
        'full-width',
      ),
      managerChild('manager.settings', paths.manager.settings, <ManagerSettingsPage />),
      managerChild('manager.billing', paths.manager.billing, <ManagerBillingPage />),
      managerChild('manager.datasets', paths.manager.datasets, <ManagerDatasetsPage />),
      managerChild('manager.autoTune', paths.manager.autoTune, <ManagerAutoTunePage />, 'full-height'),
      managerChild('manager.createDataset', paths.manager.createDataset, <ManagerDatasetCreatePage />),
      managerChild(
        'manager.exploreDataset',
        paths.manager.root + '/' + routeSegments.manager.exploreDataset,
        <ManagerDatasetExplorePage />,
      ),
      managerChild(
        'manager.editDataset',
        paths.manager.root + '/' + routeSegments.manager.editDataset,
        <ManagerDatasetEditPage />,
      ),
    ],
  },
);

export const MANAGER_ROUTES: AppRoute[] = [managerRootRoute];
